import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import ToolSeoHero from "../components/tools-seo/ToolSeoHero";
import ToolSeoBenefits from "../components/tools-seo/ToolSeoBenefits";
import ToolSeoFAQ from "../components/tools-seo/ToolSeoFAQ";
import ToolSeoCTA from "../components/tools-seo/ToolSeoCTA";
import SeoHead from "../components/seo/SeoHead";
import StructuredData, { buildFAQSchema } from "../components/seo/StructuredData";

const THEME_KEY = "hookora_theme";

const titleFaqs = [
  {
    question: "What is a YouTube title optimizer?",
    answer:
      "A YouTube title optimizer is a tool that helps improve your video titles so they feel clearer, more compelling, and more likely to attract clicks."
  },
  {
    question: "Who is Hookora’s YouTube Title Optimizer for?",
    answer:
      "It is built for YouTube creators, personal brands, marketers, and anyone who wants a faster way to generate stronger video title options."
  },
  {
    question: "Does it only work for YouTube?",
    answer:
      "This page focuses on YouTube titles, but Hookora also supports a wider creator workflow through hooks, captions, scripts, descriptions, hashtags, and more."
  },
  {
    question: "Why do video titles matter so much?",
    answer:
      "Your title is one of the first things people see before deciding whether to click. A stronger title can make your content easier to understand and more appealing to open."
  },
  {
    question: "Can this help if I already know my topic?",
    answer:
      "Yes. Even if you already know what your video is about, Hookora helps you explore stronger phrasing, cleaner wording, and better angles."
  },
  {
    question: "Is this useful for small or new channels?",
    answer:
      "Yes. New and growing channels benefit a lot from clearer titles because they help make each video idea easier for viewers to understand quickly."
  }
];

function YouTubeTitleOptimizerPage() {
  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const pageStyle = useMemo(
    () => ({
      background: isDark ? "#020617" : "#f8fafc",
      backgroundImage: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "none",
      minHeight: "100vh"
    }),
    [isDark]
  );

  return (
    <div style={pageStyle}>
      <SeoHead
        title="YouTube Title Optimizer | Hookora"
        description="Use Hookora’s YouTube Title Optimizer to create clearer, more clickable video titles with stronger wording, better structure, and faster workflow."
        path="/youtube-title-optimizer"
      />
      <StructuredData
        id="youtube-title-optimizer-faq-schema"
        data={buildFAQSchema(titleFaqs)}
      />

      <Navbar />
      <ToolSeoHero
        badge="Hookora Tool • YouTube Title Optimizer"
        title="YouTube Title Optimizer for creators who want clearer, more clickable video titles"
        description="The Hookora YouTube Title Optimizer helps creators turn rough video ideas into stronger title options. Instead of struggling to phrase your idea, you get cleaner, sharper titles designed to improve clarity, curiosity, and click potential."
        highlights={[
          "Improve click potential",
          "Write clearer titles",
          "Generate faster",
          "Built for creator workflow"
        ]}
        primaryCtaLabel="Start Free"
        primaryCtaTo="/register"
        secondaryCtaLabel="See Features"
        secondaryCtaTo="/features"
        previewTitle="What the YouTube Title Optimizer helps you do"
        previewItems={[
          {
            label: "Clarity",
            text: "Turn broad or messy topics into titles that are easier to understand at a glance."
          },
          {
            label: "Clickability",
            text: "Create title options that feel more compelling without sounding forced or spammy."
          },
          {
            label: "Consistency",
            text: "Keep your title workflow sharper when creating multiple videos or testing angles."
          }
        ]}
      />

      <ToolSeoBenefits
        eyebrow="Why creators use it"
        title="A YouTube title optimizer should help you make better title decisions, not just louder ones"
        description="A strong YouTube title helps people understand the value of your video quickly. Hookora helps you create title options that feel clearer, more intentional, and more aligned with what viewers want to click."
        benefits={[
          {
            title: "Turn rough ideas into usable title options",
            text: "When your topic is still messy, Hookora helps you shape it into titles that feel more structured and easier to publish."
          },
          {
            title: "Improve clarity without losing curiosity",
            text: "The best titles are not vague. They are clear enough to understand and strong enough to make people want more."
          },
          {
            title: "Create multiple angles from one topic",
            text: "A single video idea can support several title approaches. This gives you more room to test and choose better wording."
          },
          {
            title: "Save time in your publishing workflow",
            text: "Instead of rewriting titles over and over, you get fast starting points that reduce hesitation and keep your workflow moving."
          },
          {
            title: "Support better alignment with hooks and thumbnails",
            text: "Your title works best when it fits the rest of your content packaging. Hookora helps your message feel more connected."
          },
          {
            title: "Useful for both beginners and active creators",
            text: "Whether you are posting your first videos or building a consistent channel, stronger title options make publishing easier."
          }
        ]}
      />

      <ToolSeoFAQ
        title="YouTube Title Optimizer FAQ"
        intro="Here are a few common questions about Hookora’s YouTube Title Optimizer."
        faqs={titleFaqs}
      />

      <ToolSeoCTA
        badge="Start free with Hookora"
        title="Use Hookora’s YouTube Title Optimizer to write sharper titles with less effort"
        description="If you want better YouTube titles, cleaner wording, and a faster way to shape your video ideas, Hookora gives you a practical place to start."
        primaryLabel="Create Free Account"
        primaryTo="/register"
        secondaryLabel="Explore Features"
        secondaryTo="/features"
      />
    </div>
  );
}

export default YouTubeTitleOptimizerPage;